import { Observable, Subject } from 'rxjs';
import { map, filter, takeUntil, tap, debounceTime } from 'rxjs/operators';

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Self,
  Input,
  OnInit,
  DoCheck,
  Optional,
  OnChanges,
  OnDestroy,
  Component,
  ViewChild,
  HostBinding,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import {
  UntypedFormControl,
  ControlValueAccessor,
  FormGroupDirective,
  NgControl,
  NgForm,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatSelect, MatSelectChange } from '@angular/material/select';

import { ISnListQueryParameters } from '@lib/api';
import { SnUser } from '@lib/api/entities';
import { SnUsersAPI } from '@lib/api/services';
import { SnScrollReference } from '@lib/common/services';

class UserSelectComponentBase {
  constructor(
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    public ngControl: NgControl,
    public _defaultErrorStateMatcher: ErrorStateMatcher,
  ) {}
}

@Component({
  selector: 'sn-user-select',
  templateUrl: './user-select.component.html',
  styleUrls: ['./user-select.component.scss'],
  inputs: ['disabled'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: UserSelectComponent,
    },
  ],
})
export class UserSelectComponent
  extends UserSelectComponentBase
  implements OnChanges, OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string>
{
  @Input()
  get value(): string {
    return this._value;
  }
  set value(value: string) {
    if (value !== this.value) {
      this._value = value;
      this.stateChanges.next();
      this._controlValueAccessorChangeFn(value);
    }
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(required: boolean) {
    this._required = coerceBooleanProperty(required);
    this.stateChanges.next();
  }

  @HostBinding('class.floating')
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  get empty(): boolean {
    return !this.value;
  }

  public static nextId = 0;

  @HostBinding()
  public id = `sn-user-select-${UserSelectComponent.nextId++}`;

  @HostBinding('attr.aria-describedby')
  public describedBy = '';

  @ViewChild(MatSelect, { static: true })
  public select: MatSelect;

  public searchControl = new UntypedFormControl();

  public focused = false;
  public hidden = true;

  public controlType = 'sn-user-select';

  public stateChanges: Subject<void> = new Subject();

  public disabled: boolean;

  public errorState: boolean;

  @Input()
  set selectedOptions(value: SnUser[]) {
    this.options.push(...value);
    this.options = [...new Set<SnUser>(this.options)];
  }

  public options: SnUser[] = [];

  public isLoading = true;
  public isLoaded = false;
  public totalPages: number;

  private _value: string;
  private _placeholder: string;
  private _required = false;
  private _scrollRef: SnScrollReference;

  private _params: ISnListQueryParameters = {
    page: 1,
    pageSize: 10,
    search: null,
  };

  private readonly _destroy$ = new Subject<void>();

  constructor(
    defaultErrorStateMatcher: ErrorStateMatcher,
    @Optional() parentForm: NgForm,
    @Optional() parentFormGroup: FormGroupDirective,
    @Optional() @Self() public readonly ngControl: NgControl,
    private readonly _usersAPI: SnUsersAPI,
    private readonly _cd: ChangeDetectorRef,
  ) {
    super(parentForm, parentFormGroup, ngControl, defaultErrorStateMatcher);

    if (ngControl) {
      ngControl.valueAccessor = this;
    }

    this._fetchData();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) {
      this.stateChanges.next();
    }
  }

  public ngOnInit(): void {
    this.stateChanges.next();
    this.select.openedChange
      .pipe(
        filter(() => this.select.panelOpen),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._initSelectScroll();
      });

    this.searchControl.valueChanges
      .pipe(debounceTime(500), takeUntil(this._destroy$))
      .subscribe((name) => {
        this._params = {
          ...this._params,
          page: 1,
          search: name,
        };
        this._fetchData(true);
      });
  }

  public displayFn(user: SnUser): string {
    return user && user.fullName ? user.fullName : '';
  }

  public ngOnDestroy(): void {
    this.stateChanges.complete();
    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackUser(index: number, item: SnUser): any {
    return item.id;
  }

  public onSelected(event: MatSelectChange): void {
    this.value = event.value;
  }

  public setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  public onContainerClick(event: MouseEvent): void {
    this.select.open();
  }

  public writeValue(value: any): void {
    this.value = value;
  }

  public registerOnChange(fn: (value: any) => void): void {
    this._controlValueAccessorChangeFn = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.stateChanges.next();
  }

  public registerOnTouched(): void {}

  private _controlValueAccessorChangeFn: (value: any) => void = () => {};

  private _loadOptions(): void {
    if (this._params.page === this.totalPages) {
      return;
    }
    this._params = {
      ...this._params,
      page: this._params.page + 1,
    };
    this._fetchData();
  }

  private _fetchData(isSearching: boolean = false): void {
    this.isLoading = true;
    this._cd.markForCheck();

    this._usersAPI
      .getUserList(this._params)
      .pipe(
        tap((response) => (this.totalPages = response.totalPages)),
        map((response) => response.results),
        takeUntil(this._destroy$),
      )
      .subscribe((response) => {
        if (isSearching) {
          this.options = response;
        } else {
          this.options.push(...response);
          this.options = [...new Set<SnUser>(this.options)];
        }
        this.isLoading = false;
        this._cd.markForCheck();
      });
  }

  private _initSelectScroll(): void {
    const panelElement = this.select.panel.nativeElement;

    if (this._scrollRef) {
      this._scrollRef.container = panelElement;
    } else {
      this._scrollRef = new SnScrollReference(panelElement);
      this._scrollRef.scrolledDown$
        .pipe(
          filter(() => !this.isLoading && !this.isLoaded),
          takeUntil(this._destroy$),
        )
        .subscribe(() => this._loadOptions());
    }
  }
}
